<template>
  <div class="mensagens-programadas-lote-progress">
    <progress-bar :value="progressPercent" class="progress-bar" />
    <div class="progress-text">
      <span>{{ t('{completed} enviadas', { completed: progressData.completed }) }}</span>
      <span v-if="progressData.failed > 0" class="failed-count">{{ t(', {failed} com erro', { failed: progressData.failed }) }}</span>
      <span v-if="progressData.pending > 0" class="pending-count">{{ t(', {pending} pendentes', { pending: progressData.pending }) }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMensagensProgramadasLote } from '@/composables/useMensagensProgramadasLote';
import { log } from '@/utils/DebugLogger';

type Id = number | string;

interface ProgressDataShape {
  completed: number;
  failed: number;
  pending: number;
  processing: number;
  total: number;
}

interface ProgressApiResponse {
  completed?: number;
  sent?: number;
  failed?: number;
  pending?: number;
  processing?: number;
  total?: number;
}

interface Props {
  loteId: Id;
}

const props = defineProps<Props>();

const { t } = useI18n();

// Composables
const { fetchBulkProgress } = useMensagensProgramadasLote();

// Data
const progressData = ref<ProgressDataShape>({
  completed: 0,
  failed: 0,
  pending: 0,
  processing: 0,
  total: 0
});
let pollingInterval: ReturnType<typeof setInterval> | null = null;

// Computed
const progressPercent = computed<number>(() => {
  if (!progressData.value.total || progressData.value.total === 0) {
    return 0;
  }
  return Math.round((progressData.value.completed / progressData.value.total) * 100);
});

const isFinished = computed<boolean>(() => {
  return (progressData.value.pending + progressData.value.processing) === 0 && progressData.value.total > 0;
});

// Methods
const loadProgress = async (): Promise<void> => {
  log('MensagensProgramadasLoteProgress', 'loadProgress', 'Fetching progress for lote', props.loteId);
  try {
    const loteIdNum = typeof props.loteId === 'string' ? parseInt(props.loteId, 10) : props.loteId;
    const data = await fetchBulkProgress(loteIdNum) as ProgressApiResponse | null;
    if (data) {
      progressData.value = {
        completed: data.completed || data.sent || 0,
        failed: data.failed || 0,
        pending: data.pending || 0,
        processing: data.processing || 0,
        total: data.total || 0
      };
      log('MensagensProgramadasLoteProgress', 'loadProgress', 'Progress updated', progressData.value);

      // Stop polling when there are no more active items
      if (isFinished.value) {
        stopPolling();
      }
    }
  } catch (err) {
    log('MensagensProgramadasLoteProgress', 'loadProgress', 'Error fetching progress', err);
  }
};

const startPolling = (): void => {
  if (pollingInterval) {
    return;
  }
  log('MensagensProgramadasLoteProgress', 'startPolling', 'Starting progress polling every 5 seconds');
  pollingInterval = setInterval(() => {
    loadProgress();
  }, 5000);
};

const stopPolling = (): void => {
  if (pollingInterval) {
    log('MensagensProgramadasLoteProgress', 'stopPolling', 'Stopping progress polling');
    clearInterval(pollingInterval);
    pollingInterval = null;
  }
};

// Lifecycle
onMounted(() => {
  log('MensagensProgramadasLoteProgress', 'onMounted', 'Component mounted for lote', props.loteId);
  loadProgress();
  startPolling();
});

onUnmounted(() => {
  log('MensagensProgramadasLoteProgress', 'onUnmounted', 'Component unmounted');
  stopPolling();
});
</script>

<style scoped>
.mensagens-programadas-lote-progress {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.progress-bar {
  height: 8px;
}

.progress-text {
  font-size: 0.75rem;
  color: var(--text-color-secondary);
}

.failed-count {
  color: var(--red-500);
}

.pending-count {
  color: var(--text-color-secondary);
}
</style>
